import type {
  DataSourceDeclaration,
  HttpDataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { isHttpDataSourceDeclaration } from '@de/utils/data-sources/data-source-kind';
import type { ApiConfig } from '@de/types/config';
import type { InterpolatedRequest } from '../data-sources/fetchDataSource';

/**
 * Per-service credential attachment for outbound DSM requests.
 *
 * Some upstream services (Mobius today; Audience-Intent later)
 * authenticate every request against the host's publisher identity
 * token rather than running through the platform's M2M flow. The
 * token lives on `siteConfig.identityToken` (populated by the host's
 * tenant-config fetch); requiring every DSL author to thread it into
 * each `query`/`headers` block by hand is brittle and error-prone --
 * miss one and the request lands `403 AuthError` before the body is
 * even decoded.
 *
 * `attachServiceCredentials` walks a fully-resolved declaration map
 * (after {@link resolveDataSourceDeclarations} has promoted path-only
 * endpoints to absolute URLs) and, when the declaration's origin
 * matches a service whose policy requires a credential, mixes the
 * credential into the declaration's `query` (or `headers`, for
 * services that prefer that form). Author-supplied values always
 * win on a key collision -- the auto-attach is a default, not a
 * mandate.
 *
 * The function is pure -- it returns a new map of new declaration
 * objects when any change is needed, and the input itself when no
 * declaration matches a configured service. Callers should memoize
 * on `[decls, apiConfig, identityToken]`.
 */
export function attachServiceCredentials(
  decls: Readonly<Record<string, DataSourceDeclaration>>,
  apiConfig: ApiConfig,
  credentials: ServiceCredentials,
): Readonly<Record<string, DataSourceDeclaration>> {
  const policies = collectPolicies(apiConfig, credentials);
  if (policies.length === 0) return decls;

  const out: Record<string, DataSourceDeclaration> = { ...decls };
  let changed = false;

  for (const [name, decl] of Object.entries(decls)) {
    if (!isHttpDataSourceDeclaration(decl)) {
      continue;
    }
    const origin = extractOrigin(decl.endpoint);
    if (!origin) continue;

    const policy = policies.find((p) => p.origin === origin);
    if (!policy) continue;

    const next = policy.apply(decl);
    if (next !== decl) {
      out[name] = next;
      changed = true;
    }
  }

  return changed ? out : decls;
}

/**
 * Bag of host-supplied credentials. Today only the publisher
 * `identityToken` (Mobius `?idToken=…`) is recognized; additional
 * services slot in here without changing the bridge call site.
 */
export interface ServiceCredentials {
  readonly identityToken?: string;
}

/**
 * Internal -- attachment policy resolved against `apiConfig` so
 * `attachServiceCredentials` can iterate declarations and match by
 * origin in O(decls × policies). Each policy carries the origin to
 * match plus the mutation it applies to a declaration.
 */
interface AttachmentPolicy {
  readonly origin: string;
  readonly apply: (
    decl: HttpDataSourceDeclaration,
  ) => HttpDataSourceDeclaration;
}

function collectPolicies(
  apiConfig: ApiConfig,
  credentials: ServiceCredentials,
): readonly AttachmentPolicy[] {
  const policies: AttachmentPolicy[] = [];

  // Mobius: publisher idToken is accepted as `?idToken=…` per
  // `gha-mobius/cmd/mobius-api/middleware.go::ExtractToken`. The
  // existing `MobiusApiClient.getLocationInference()` uses the same
  // form; matching it keeps the request shape identical between the
  // legacy client and DSM-driven calls.
  const mobiusOrigin = extractOrigin(apiConfig.mobiusApiUrl);
  if (mobiusOrigin && credentials.identityToken) {
    const token = credentials.identityToken;
    policies.push({
      origin: mobiusOrigin,
      apply: (decl) => attachQueryParam(decl, 'idToken', token),
    });
  }

  return policies;
}

function attachQueryParam(
  decl: HttpDataSourceDeclaration,
  key: string,
  value: string,
): HttpDataSourceDeclaration {
  const existing = decl.query as
    | Readonly<Record<string, string | readonly string[]>>
    | undefined;
  if (existing && Object.prototype.hasOwnProperty.call(existing, key)) {
    // Author already specified the credential -- their value wins.
    // This lets a step override the publisher token (e.g. test
    // fixtures) without the bridge clobbering the choice.
    return decl;
  }
  return {
    ...decl,
    query: { ...(existing ?? {}), [key]: value },
  };
}

/**
 * Build a post-interpolation request transform that enforces Mobius's
 * type contract on inbound DSL requests.
 *
 * Why this is a separate, post-interpolation transform:
 *   - The DSL `body` is templated (`{{formState.degreeId}}`) at decl
 *     build time, so coercion can't be baked into
 *     `attachServiceCredentials`'s decl-rewrite step — the value is
 *     literally a `"{{…}}"` string at that point.
 *   - DSM's `formState` stores every value as a string (canonical:
 *     dynamic-experiences/src/providers/DataSourceManager.tsx —
 *     `Record<string, string>`). The interpolator faithfully drops
 *     those strings into the resolved body. Mobius's
 *     `JSONArrayableInt64` type
 *     (gha-mobius/application/mobius/lib/utils/json_arrayable_int.go)
 *     accepts only raw integers / arrays of integers — string
 *     literals like `"2"` produce `422 Unprocessable Entity`. This
 *     hits Mobius's `Criteria.{DegreeID,CategoryID,SubjectID,SchoolID}`
 *     fields specifically.
 *   - The fix is service-shaped: numeric IDs going *to Mobius* must
 *     be numbers. It is not a general DSM concern, and the runtime
 *     ID-typing rules differ across services. So the rule lives next
 *     to the other Mobius-specific bridge knowledge
 *     (`attachServiceCredentials`'s idToken policy) rather than in
 *     `fetchDataSource`.
 *
 * Returns `undefined` when no Mobius origin is configured — callers
 * can pass the result straight to DSM without a null branch.
 *
 * The transform is a pure function `(req) => req`. When the request's
 * resolved origin doesn't match Mobius, or the body has no
 * `criteria` block, or the relevant fields are absent, it returns
 * the input unchanged.
 */
export function buildMobiusBodyCoercer(
  apiConfig: ApiConfig,
): ((req: InterpolatedRequest) => InterpolatedRequest) | undefined {
  const mobiusOrigin = extractOrigin(apiConfig.mobiusApiUrl);
  if (!mobiusOrigin) return undefined;

  return (req) => {
    const reqOrigin = extractOrigin(req.endpoint);
    if (reqOrigin !== mobiusOrigin) return req;
    const coercedBody = coerceMobiusCriteria(req.body);
    if (coercedBody === req.body) return req;
    return { ...req, body: coercedBody };
  };
}

/**
 * The DCS taxonomy namespaces (degree / category / subject) and the
 * `general` node they fall back to. These are the Offerings taxonomy
 * keys the DCS triad `formState` fields (`degree` / `category` /
 * `subject`) map to; `general` is the Offerings node labeled
 * "Undecided". An unselected DCS dimension resolves to this node so the
 * results page surfaces general programs rather than dropping the
 * dimension (which would broaden to the whole corpus).
 */
const DCS_TAXONOMY_NAMESPACES: ReadonlySet<string> = new Set([
  'degree_level',
  'academic_subject_category',
  'academic_subject',
]);
const DCS_GENERAL_NODE = 'general';

/**
 * Build a post-interpolation request transform that sanitizes the
 * Offerings search `taxonomy` block so an unselected dimension can't
 * ship an invalid filter.
 *
 * Why this is needed:
 *   - StudyMatch-style result pages template the taxonomy as
 *     single-element arrays: `"degree_level": ["{{formState.degree}}"]`.
 *     The interpolator resolves each single-expression element to the
 *     raw `formState` value, so a dimension the user left unselected
 *     (no seed — e.g. a GGG page where the `general` sentinel is
 *     intentionally dropped from the DSM seed) resolves to `undefined`,
 *     leaving `["degree_level": [undefined]]`. `JSON.stringify` turns
 *     that into `[null]`.
 *   - Offerings `POST /v1/offerings/search` validates `taxonomy` as
 *     `Record<string, string[]>` (every value a non-empty array of
 *     strings) and rejects `[null]` with `400 Bad Request`
 *     ("taxonomy must be an object whose values are arrays of
 *     strings"). The unresolved-dependency guard doesn't help here: it
 *     only defers URL-shaping fields (endpoint/headers/query), not the
 *     body, on the assumption that JSON drops `undefined` object
 *     fields — which the array wrapper defeats.
 *
 * The fix drops non-string / empty / whitespace-only entries from each
 * taxonomy dimension array. When nothing valid survives:
 *   - a **DCS** dimension (degree / category / subject) falls back to
 *     the `general` node, so an undecided/undecided/undecided triad
 *     ships `{ degree_level: ["general"], academic_subject_category:
 *     ["general"], academic_subject: ["general"] }` and the results
 *     page shows general programs (matching the platform intent that
 *     the submit path substitutes `general`);
 *   - any **other** namespace is dropped entirely.
 * Real selections (`["bachelors"]`) pass through untouched.
 *
 * Scope mirrors {@link buildMobiusBodyCoercer}: the rule is
 * service-shaped (Offerings' taxonomy contract), so it lives in the
 * bridge rather than in `fetchDataSource`. It runs only on POSTs to an
 * Offerings origin whose body carries a `taxonomy` object; every other
 * request passes through unchanged. Returns `undefined` when no
 * Offerings origin is configured, so callers can pass the result
 * straight through without a null branch.
 */
export function buildOfferingsTaxonomyBodyCoercer(
  apiConfig: ApiConfig,
): ((req: InterpolatedRequest) => InterpolatedRequest) | undefined {
  const offeringsOrigins = new Set(
    [apiConfig.offeringsServiceBaseUrl, apiConfig.offeringsSearchApiUrl]
      .map(extractOrigin)
      .filter((origin): origin is string => origin !== null),
  );
  if (offeringsOrigins.size === 0) return undefined;

  return (req) => {
    if (req.method === 'GET') return req;
    const reqOrigin = extractOrigin(req.endpoint);
    // Absolute endpoints (production shape after declaration resolution)
    // must target an Offerings origin; path-only endpoints carry no
    // origin and are gated on the taxonomy-body check alone.
    if (reqOrigin && !offeringsOrigins.has(reqOrigin)) return req;
    const coercedBody = sanitizeOfferingsTaxonomy(req.body);
    if (coercedBody === req.body) return req;
    return { ...req, body: coercedBody };
  };
}

function sanitizeOfferingsTaxonomy(body: unknown): unknown {
  if (!isPlainObject(body)) return body;
  const taxonomy = body['taxonomy'];
  if (!isPlainObject(taxonomy)) return body;

  let nextTaxonomy: Record<string, unknown> | null = null;
  for (const [namespace, value] of Object.entries(taxonomy)) {
    const cleaned = cleanTaxonomyDimension(value, namespace);
    if (cleaned === value) continue;
    nextTaxonomy ??= { ...taxonomy };
    if (cleaned === undefined) {
      delete nextTaxonomy[namespace];
    } else {
      nextTaxonomy[namespace] = cleaned;
    }
  }

  if (!nextTaxonomy) return body;
  return { ...body, taxonomy: nextTaxonomy };
}

/**
 * Filter a single taxonomy dimension down to its valid node keys.
 *
 * Returns the original reference when nothing changes, and a new array
 * when entries are dropped. When no valid entry survives, a DCS
 * dimension (degree / category / subject) resolves to `["general"]`
 * (the "Undecided" node) and any other namespace resolves to
 * `undefined` (signal to drop the whole dimension). Non-array values
 * pass through untouched — the coercer only repairs the array-wrapped
 * template shape and leaves other author mistakes for the service to
 * reject.
 */
function cleanTaxonomyDimension(value: unknown, namespace: string): unknown {
  if (!Array.isArray(value)) return value;
  let changed = false;
  const filtered: string[] = [];
  for (const item of value) {
    if (typeof item === 'string' && item.trim().length > 0) {
      filtered.push(item);
    } else {
      changed = true;
    }
  }
  if (filtered.length === 0) {
    return DCS_TAXONOMY_NAMESPACES.has(namespace)
      ? [DCS_GENERAL_NODE]
      : changed
        ? undefined
        : value;
  }
  return changed ? filtered : value;
}

/**
 * Compose several post-interpolation request-body transforms into one,
 * applied left-to-right. `undefined` entries (a builder that found no
 * matching origin) are skipped. Returns `undefined` when nothing is
 * left to apply so callers can pass the result straight to DSM without
 * a null branch. Each transform is pure `(req) => req`, so the
 * composition is too.
 */
export function composeRequestBodyCoercers(
  ...coercers: ReadonlyArray<
    ((req: InterpolatedRequest) => InterpolatedRequest) | undefined
  >
): ((req: InterpolatedRequest) => InterpolatedRequest) | undefined {
  const active = coercers.filter(
    (c): c is (req: InterpolatedRequest) => InterpolatedRequest =>
      c !== undefined,
  );
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  return (req) => active.reduce((acc, coerce) => coerce(acc), req);
}

/**
 * Mobius `MobiusCriteria` schema fields whose ID type is
 * `JSONArrayableInt64` (scalar int64 OR array<int64>). The coercer
 * only touches these — every other criteria field passes through
 * untouched so we never widen the rule beyond Mobius's typed schema.
 *
 * Source-of-truth:
 *   gha-mobius/application/mobius/cmd/mobius-api/degrees.go
 *   `type DegreesCriteria struct { ... }`
 *
 * Scalar fields (`degreeId`, `categoryId`, `subjectId`) coerce
 * numeric strings to numbers. The array field (`schoolId`) coerces
 * each numeric-string element to a number.
 */
const MOBIUS_NUMERIC_CRITERIA_SCALARS = [
  'degreeId',
  'categoryId',
  'subjectId',
] as const;
const MOBIUS_NUMERIC_CRITERIA_ARRAYS = ['schoolId'] as const;

function coerceMobiusCriteria(body: unknown): unknown {
  if (!isPlainObject(body)) return body;
  const criteria = body['criteria'];
  if (!isPlainObject(criteria)) return body;

  let nextCriteria: Record<string, unknown> | null = null;
  for (const field of MOBIUS_NUMERIC_CRITERIA_SCALARS) {
    if (!(field in criteria)) continue;
    const original = criteria[field];
    const coerced = coerceToInt(original);
    if (coerced === original) continue;
    nextCriteria ??= { ...criteria };
    nextCriteria[field] = coerced;
  }
  for (const field of MOBIUS_NUMERIC_CRITERIA_ARRAYS) {
    if (!(field in criteria)) continue;
    const original = criteria[field];
    if (!Array.isArray(original)) continue;
    let arrayChanged = false;
    const coercedArray = original.map((item) => {
      const c = coerceToInt(item);
      if (c !== item) arrayChanged = true;
      return c;
    });
    if (!arrayChanged) continue;
    nextCriteria ??= { ...criteria };
    nextCriteria[field] = coercedArray;
  }

  if (!nextCriteria) return body;
  return { ...body, criteria: nextCriteria };
}

/**
 * Coerce a numeric-string leaf to a number. Pass-through for:
 *   - already-numeric values
 *   - strings that don't look like a finite integer (`""`, `"abc"`,
 *     `"1.5"`, `"1e10"` — Mobius wants integer ids, not floats or
 *     scientific notation)
 *   - any non-string, non-number value (booleans, null, objects)
 *
 * The conservative match — `^-?\d+$` plus a `Number.isSafeInteger`
 * guard on the parsed result — ensures we never silently corrupt a
 * value the author plausibly meant as a string (e.g. an opaque id),
 * and we never produce an unsafe-integer Number that JSON would
 * round-trip incorrectly.
 */
function coerceToInt(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  if (!/^-?\d+$/.test(value)) return value;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) return value;
  return parsed;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.origin.toLowerCase();
  } catch {
    return null;
  }
}
